import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import {
    BodyTextMedium,
    Button,
    HeadingText20,
    HeadingText50,
    Modal as DsModal,
    pxToRem,
} from '@modernhealth/vital-components';

import { Benefit } from '../models/benefits';
import { BenefitModalTestID } from '../models/testsIds';

interface Props {
    onClose: VoidFunction;
    benefit: Benefit | undefined;
    visible: boolean;
}

const ContentWrapper = styled.div({ padding: pxToRem(32) });

const SectionContainer = styled.div({ paddingTop: pxToRem(16) });

const ButtonContainer = styled.div({
    paddingTop: '32px',
});

interface LogoContainerProp {
    color: string | undefined;
}

const HeaderRow = styled.div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: pxToRem(16),
});

const HeaderContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginLeft: pxToRem(8),
});

const LogoWrapper = styled.div<LogoContainerProp>(({ color }) => {
    return {
        height: pxToRem(60),
        minWidth: pxToRem(60),
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        backgroundColor: color,
        borderRadius: pxToRem(6),
        marginRight: pxToRem(14),
    };
});

const LogoImage = styled.img({
    height: pxToRem(38),
    width: pxToRem(38),
});

const PhoneNumberContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginTop: pxToRem(32),
});

export const BenefitModal: React.FC<Props> = ({ visible, onClose, benefit }) => {
    const { t } = useTranslation();
    return (
        <>
            {benefit && (
                <React.Fragment>
                    <DsModal
                        maxWidth={600}
                        isVisible={visible}
                        onCloseButtonClick={onClose}
                        noPaddings
                    >
                        <ContentWrapper data-testid={BenefitModalTestID.Modal}>
                            <HeaderRow>
                                <LogoWrapper color={benefit.color}>
                                    <LogoImage src={benefit.imageUrl} alt="" />
                                </LogoWrapper>

                                <HeaderContent>
                                    <HeadingText50 marginBottom={0}>{benefit.title}</HeadingText50>
                                    <BodyTextMedium>{benefit.providerName}</BodyTextMedium>
                                </HeaderContent>
                            </HeaderRow>

                            <SectionContainer data-testid={BenefitModalTestID.About}>
                                <HeadingText20 marginBottom={4}>
                                    {t('benefits:benefit_modal.about')}
                                </HeadingText20>
                                <BodyTextMedium>{benefit.description}</BodyTextMedium>
                                {benefit.phoneNumber && (
                                    <PhoneNumberContainer
                                        data-testid={BenefitModalTestID.PhoneNumber}
                                    >
                                        <HeadingText20 marginBottom={4}>
                                            {t('benefits:benefit_modal.phone_number')}
                                        </HeadingText20>
                                        <BodyTextMedium>{benefit.phoneNumber}</BodyTextMedium>
                                    </PhoneNumberContainer>
                                )}
                            </SectionContainer>

                            <ButtonContainer data-testid={BenefitModalTestID.Button}>
                                <Button
                                    size="large"
                                    variant="primary"
                                    test-id={BenefitModalTestID.Button}
                                    fluid
                                    onClick={() => window.open(benefit.link)}
                                >
                                    {t('benefits:benefit_card.learn_more')}
                                </Button>
                            </ButtonContainer>
                        </ContentWrapper>
                    </DsModal>
                </React.Fragment>
            )}
        </>
    );
};
